import { AuthContext } from '../../context/AuthContext';
import { LangProvider, useLang } from '../../context/LangContext';
import { useEffect, useContext, useState } from 'react';
import esTexts from '../../translations/es.json';
import enTexts from '../../translations/en.json';
import axios from 'axios';
import Login from '../Login/index';
import { Link, Navigate } from 'react-router-dom';
import Filters from '../../components/Filters/FiltersHome';
import Welcome from '../../components/Header/Welcome';
import PreviewDoc from '../../components/PreviewDoc';
import PreviewProperty from '../../components/PreviewProperty';
import { AdminNav } from '../../components';

function Admin() {
  const { token, currentUser } = useContext(AuthContext);
  const isAdmin = Boolean(currentUser?.isAdmin);

  const [errorUpdate, setErrorUpdate] = useState(false);
  const [successUpdate, setSuccessUpdate] = useState(false);
  const [errorUpdateMessage, setErrorUpdateMessage] = useState('');
  const [successUpdateMessage, setSuccessUpdateMessage] = useState('');

  const [accommodations, setAccommodations] = useState([]);
  const [filteredAccommodations, setFilteredAccommodations] = useState([]);

  const [filterCountry, setFilterCountry] = useState(null);
  const [filterRegion, setFilterRegion] = useState(null);
  const [filterCity, setFilterCity] = useState(null);

  const [draggedItemId, setDraggedItemId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { lang } = useLang();
  const texts = lang === 'es' ? esTexts : enTexts;

  const getAllAccommodations = async () => {
    // if (!token) return;
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/accommodations/allAdmin`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
          },
        }
      );

      setAccommodations(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccommodation = async id => {
    // show confirm popup
    const confirm = window.confirm('¿Está seguro de eliminar este alojamiento?');
    if (!confirm) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/accommodations/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // show success message
      if (response.status === 200 || response.status === 201) {
        setSuccessUpdate(true);
        setSuccessUpdateMessage(response.data.message);
      } else {
        setErrorUpdate(true);
        setErrorUpdateMessage('Ups!');
      }

      // get all accommodations
      getAllAccommodations();
    } catch (error) {
      console.log(error);
    }
  };

  const handleSwitchStatus = async id => {
    // show confirm popup
    const confirm = window.confirm('¿Está seguro de cambiar el estado de este alojamiento?');
    if (!confirm) return;

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/accommodations/${id}/updateDisplay`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSuccessUpdate(true);
        setSuccessUpdateMessage(response.data.message);
      } else {
        setErrorUpdate(true);
        setErrorUpdateMessage('Ups!');
      }
      getAllAccommodations();
    } catch (error) {
      setErrorUpdate(true);
      setErrorUpdateMessage('Ups!');
      console.log(error);
    }
  };

  const filterAccommodations = () => {
    let filtered = accommodations;

    if (filterCountry) {
      filtered = filtered.filter(accommodation => {
        const cId =
          accommodation.country_id ||
          (accommodation.city &&
            accommodation.city.region &&
            accommodation.city.region.country &&
            accommodation.city.region.country.id);
        return Number(cId) === Number(filterCountry);
      });
    }

    if (filterRegion) {
      filtered = filtered.filter(accommodation => accommodation.region_id === filterRegion);
    }

    if (filterCity) {
      filtered = filtered.filter(accommodation => accommodation.city_id === filterCity);
    }

    setFilteredAccommodations(filtered);
  };

  useEffect(() => {
    if (token) {
      getAllAccommodations();
    }
  }, [token]);

  const handleCountrySelected = selectedOption => {
    setFilterCountry(selectedOption);
  };
  const handleRegionSelected = selectedOption => {
    setFilterRegion(selectedOption);
  };
  const handleCiudadSelected = selectedOption => {
    setFilterCity(selectedOption);
  };

  const moveItem = async (index, direction) => {
    const newIndex = index + direction;
    const items =
      filterCountry || filterRegion || filterCity ? filteredAccommodations : accommodations;

    if (newIndex < 0 || newIndex >= items.length) return;

    const itemToMove = items[index];
    const targetItem = items[newIndex];

    let masterReordered = [...accommodations];
    const masterIndexToMove = masterReordered.findIndex(item => item.id === itemToMove.id);
    const masterTargetIndex = masterReordered.findIndex(item => item.id === targetItem.id);

    if (masterIndexToMove !== -1 && masterTargetIndex !== -1) {
      const [removed] = masterReordered.splice(masterIndexToMove, 1);
      masterReordered.splice(masterTargetIndex, 0, removed);
    }

    setAccommodations(masterReordered);

    const newItems = masterReordered.map((item, idx) => {
      return { id: item.id, position: idx + 1 };
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/${lang}/accommodations/updatePositions`,
        { positions: newItems },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200 || response.status === 201) {
        setSuccessUpdate(true);
        setSuccessUpdateMessage(response.data.message);
      } else {
        setErrorUpdate(true);
        setErrorUpdateMessage('Ups!');
      }
    } catch (error) {
      setErrorUpdate(true);
      setErrorUpdateMessage('Ups!');
      console.log(error);
    }
  };

  useEffect(() => {
    filterAccommodations();
  }, [filterCountry, filterRegion, filterCity, accommodations]);

  if (!token) {
    return <Login />;
  }

  return (
    <>
      {/* Header con Logos */}
      <Welcome />

      <div className="container py-4">
        {isAdmin && <AdminNav />}

        {/* Page Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 pb-3 border-bottom gap-2">
          <div>
            <h3 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
              <i className="fa fa-building-o text-sen"></i>
              <span>{texts.admin.title_list}</span>
            </h3>
            <p className="text-muted small mb-0">
              {lang === 'es'
                ? 'Monitorea, ordena y gestiona el estado y documentos de los alojamientos certificados.'
                : 'Monitor, order, and manage verified accommodation listings and certifications.'}
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-white text-secondary border px-3 py-2 rounded-pill shadow-xs fw-semibold">
              <i className="fa fa-home me-1 text-sen"></i>
              {lang === 'es'
                ? `${
                    (filterCountry || filterRegion || filterCity
                      ? filteredAccommodations
                      : accommodations
                    ).length
                  } alojamientos`
                : `${
                    (filterCountry || filterRegion || filterCity
                      ? filteredAccommodations
                      : accommodations
                    ).length
                  } listings`}
            </span>
          </div>
        </div>

        {/* Filters Toolbar */}
        <div className="mb-3">
          <Filters
            onCountrySelected={handleCountrySelected}
            onRegionSelected={handleRegionSelected}
            onCiudadSelected={handleCiudadSelected}
            accommodations={accommodations}
          />
        </div>

        <div className="">
          {/* Mobile View */}
          <div className="d-block d-sm-none">
            {isLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
              </div>
            ) : (
              (filterCountry || filterRegion || filterCity
                ? filteredAccommodations
                : accommodations
              ).map((accommodation, index) => {
                const list =
                  filterCountry || filterRegion || filterCity
                    ? filteredAccommodations
                    : accommodations;
                return (
                  <div key={accommodation.id} className="mt-4 table-responsive">
                    <h5 className="text-uppercase fw-bold text-dark mb-2">{accommodation.title}</h5>
                    <table className="table table-striped table-hover border rounded-3 overflow-hidden">
                      <thead className="table-light">
                        <tr>
                          <th>
                            <div className="mb-2 fw-bold">{texts.admin.residential}</div>
                            {isAdmin && (
                              <div className="d-flex align-items-center">
                                <span
                                  onClick={() => index > 0 && moveItem(index, -1)}
                                  className={`reorder-btn me-1 ${index === 0 ? 'disabled' : ''}`}
                                  title={lang === 'es' ? 'Mover arriba' : 'Move up'}
                                >
                                  <i className="fa fa-arrow-up"></i>
                                </span>
                                <span
                                  onClick={() => index < list.length - 1 && moveItem(index, 1)}
                                  className={`reorder-btn ${
                                    index === list.length - 1 ? 'disabled' : ''
                                  }`}
                                  title={lang === 'es' ? 'Mover abajo' : 'Move down'}
                                >
                                  <i className="fa fa-arrow-down"></i>
                                </span>
                              </div>
                            )}
                          </th>
                          <th className="bg-sen-light text-center">{texts.admin.company_reg}</th>
                          <th className="text-center">{texts.admin.certificates}</th>
                          <th className="bg-sen-light text-center">{texts.admin.contract}</th>
                          {isAdmin && <th className="text-center">{texts.admin.show}</th>}
                          {isAdmin && <th className="text-end">{texts.admin.options}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        <tr key={accommodation.id}>
                          <td>{accommodation.title}</td>
                          <td className="text-center bg-sen-light">
                            {accommodation.path_erut_na ? (
                              'N/A'
                            ) : (
                              <>
                                {accommodation.path_erut ? (
                                  <>
                                    {accommodation.path_erut_expired ? (
                                      <PreviewDoc
                                        filePath={accommodation.path_erut}
                                        status="pending"
                                      />
                                    ) : (
                                      <PreviewDoc filePath={accommodation.path_erut} status="ok" />
                                    )}
                                  </>
                                ) : (
                                  <i className="fa fa-times-circle text-danger"></i>
                                )}
                              </>
                            )}
                          </td>
                          <td className="text-center">
                            {accommodation.path_te1_na ? (
                              'N/A'
                            ) : (
                              <>
                                {accommodation.path_te1 ? (
                                  <>
                                    {accommodation.path_te1_expired ? (
                                      <PreviewDoc
                                        filePath={accommodation.path_te1}
                                        status="pending"
                                      />
                                    ) : (
                                      <PreviewDoc filePath={accommodation.path_te1} status="ok" />
                                    )}
                                  </>
                                ) : (
                                  <i className="fa fa-times-circle text-danger"></i>
                                )}
                              </>
                            )}
                          </td>
                          <td className="bg-sen-light text-center">
                            {accommodation.path_contrato_na ? (
                              'N/A'
                            ) : (
                              <>
                                {accommodation.path_contrato ? (
                                  <>
                                    {accommodation.path_contrato_expired ? (
                                      <PreviewDoc
                                        filePath={accommodation.path_contrato}
                                        status="pending"
                                      />
                                    ) : (
                                      <PreviewDoc
                                        filePath={accommodation.path_contrato}
                                        status="ok"
                                      />
                                    )}
                                  </>
                                ) : (
                                  <i className="fa fa-times-circle text-danger"></i>
                                )}
                              </>
                            )}
                          </td>
                          {isAdmin && (
                            <td className="text-center">
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input mt-2 float-none"
                                  type="checkbox"
                                  role="switch"
                                  id="flexSwitchCheckChecked"
                                  checked={accommodation.visible}
                                  onChange={() => handleSwitchStatus(accommodation.id)}
                                />
                              </div>
                            </td>
                          )}
                          {isAdmin && (
                            <td className="text-end text-nowrap">
                              <Link
                                to={`/admin/edit-accommodation/${accommodation.id}`}
                                className="btn btn-sm btn-outline-primary border-0 rounded-circle me-1 p-2 shadow-none"
                                title={lang === 'es' ? 'Editar' : 'Edit'}
                              >
                                <i className="fa fa-edit"></i>
                              </Link>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger border-0 rounded-circle p-2 shadow-none"
                                onClick={() => handleDeleteAccommodation(accommodation.id)}
                                title={lang === 'es' ? 'Eliminar' : 'Delete'}
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            </td>
                          )}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })
            )}
          </div>
          {/* Desktop Table View */}
          <div className="d-none d-sm-block">
            <div className="card border-0 shadow-sm rounded-4 bg-white overflow-hidden">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light border-bottom text-secondary small text-uppercase">
                    <tr>
                      <th className="ps-3 py-3" style={{ minWidth: '220px' }}>
                        {texts.admin.residential}
                      </th>
                      <th className="bg-sen-light text-center py-3">{texts.admin.company_reg}</th>
                      <th className="text-center py-3">{texts.admin.certificates}</th>
                      <th className="bg-sen-light text-center py-3">{texts.admin.contract}</th>
                      {isAdmin && <th className="text-center py-3">{texts.admin.show}</th>}
                      {isAdmin && <th className="text-end pe-3 py-3">{texts.admin.options}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-5">
                          <div className="spinner-border text-primary mb-2" role="status">
                            <span className="visually-hidden">{texts.admin.loading}</span>
                          </div>
                          <div className="text-muted small">
                            {lang === 'es' ? 'Cargando alojamientos...' : 'Loading listings...'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      (filterCountry || filterRegion || filterCity
                        ? filteredAccommodations
                        : accommodations
                      ).map((accommodation, index) => {
                        const list =
                          filterCountry || filterRegion || filterCity
                            ? filteredAccommodations
                            : accommodations;
                        return (
                          <tr key={accommodation.id}>
                            <td className="ps-3">
                              <div className="d-flex align-items-center gap-2">
                                {isAdmin && (
                                  <div className="d-flex align-items-center flex-shrink-0">
                                    <span
                                      onClick={() => index > 0 && moveItem(index, -1)}
                                      className={`reorder-btn me-1 ${
                                        index === 0 ? 'disabled' : ''
                                      }`}
                                      title={lang === 'es' ? 'Mover arriba' : 'Move up'}
                                    >
                                      <i className="fa fa-arrow-up"></i>
                                    </span>
                                    <span
                                      onClick={() => index < list.length - 1 && moveItem(index, 1)}
                                      className={`reorder-btn ${
                                        index === list.length - 1 ? 'disabled' : ''
                                      }`}
                                      title={lang === 'es' ? 'Mover abajo' : 'Move down'}
                                    >
                                      <i className="fa fa-arrow-down"></i>
                                    </span>
                                  </div>
                                )}
                                <div className="flex-grow-1 min-w-0">
                                  <PreviewProperty accommodation={accommodation} />
                                </div>
                              </div>
                            </td>
                            <td className="text-center bg-sen-light">
                              {accommodation.path_erut_na ? (
                                <span className="badge bg-secondary-subtle text-secondary">
                                  N/A
                                </span>
                              ) : (
                                <>
                                  {accommodation.path_erut ? (
                                    <>
                                      {accommodation.path_erut_expired ? (
                                        <PreviewDoc
                                          filePath={accommodation.path_erut}
                                          status="pending"
                                        />
                                      ) : (
                                        <PreviewDoc
                                          filePath={accommodation.path_erut}
                                          status="ok"
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <i
                                      className="fa fa-times-circle text-danger"
                                      style={{ fontSize: '1.1rem' }}
                                    ></i>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="text-center">
                              {accommodation.path_te1_na ? (
                                <span className="badge bg-secondary-subtle text-secondary">
                                  N/A
                                </span>
                              ) : (
                                <>
                                  {accommodation.path_te1 ? (
                                    <>
                                      {accommodation.path_te1_expired ? (
                                        <PreviewDoc
                                          filePath={accommodation.path_te1}
                                          status="pending"
                                        />
                                      ) : (
                                        <PreviewDoc filePath={accommodation.path_te1} status="ok" />
                                      )}
                                    </>
                                  ) : (
                                    <i
                                      className="fa fa-times-circle text-danger"
                                      style={{ fontSize: '1.1rem' }}
                                    ></i>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="bg-sen-light text-center">
                              {accommodation.path_contrato_na ? (
                                <span className="badge bg-secondary-subtle text-secondary">
                                  N/A
                                </span>
                              ) : (
                                <>
                                  {accommodation.path_contrato ? (
                                    <>
                                      {accommodation.path_contrato_expired ? (
                                        <PreviewDoc
                                          filePath={accommodation.path_contrato}
                                          status="pending"
                                        />
                                      ) : (
                                        <PreviewDoc
                                          filePath={accommodation.path_contrato}
                                          status="ok"
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <i
                                      className="fa fa-times-circle text-danger"
                                      style={{ fontSize: '1.1rem' }}
                                    ></i>
                                  )}
                                </>
                              )}
                            </td>
                            {isAdmin && (
                              <td className="text-center">
                                <div className="form-check form-switch d-inline-block">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id={`switch-${accommodation.id}`}
                                    checked={Boolean(accommodation.visible)}
                                    onChange={() => handleSwitchStatus(accommodation.id)}
                                    title={lang === 'es' ? 'Mostrar / Ocultar' : 'Show / Hide'}
                                  />
                                </div>
                              </td>
                            )}
                            {isAdmin && (
                              <td className="text-end pe-3 text-nowrap">
                                <Link
                                  to={`/admin/edit-accommodation/${accommodation.id}`}
                                  className="btn btn-sm btn-outline-primary border-0 rounded-circle me-1 p-2 shadow-none"
                                  title={
                                    lang === 'es' ? 'Editar alojamiento' : 'Edit accommodation'
                                  }
                                >
                                  <i className="fa fa-pencil"></i>
                                </Link>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger border-0 rounded-circle p-2 shadow-none"
                                  onClick={() => handleDeleteAccommodation(accommodation.id)}
                                  title={
                                    lang === 'es' ? 'Eliminar alojamiento' : 'Delete accommodation'
                                  }
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </td>
                            )}
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {errorUpdate && (
          <div
            className="alert alert-danger cursor-pointer"
            role="alert"
            onClick={() => setSuccessUpdate(false)}
          >
            {errorUpdateMessage}
          </div>
        )}
        {successUpdate && (
          <div
            className="alert alert-success cursor-pointer"
            role="alert"
            onClick={() => setSuccessUpdate(false)}
          >
            {successUpdateMessage}
          </div>
        )}
      </div>
    </>
  );
}

export default Admin;
